import { Routes } from '@angular/router';

import { adminRoutes } from '@perval/admin';
import { Role } from '@perval/shared';
import { ForgotPasswordComponent } from './authentification/components/forgot-password/forgot-password.component';
import { OauthCallbackComponent } from './authentification/components/oauth-callback/oauth-callback.component';
import { ResetPasswordComponent } from './authentification/components/reset-password/reset-password.component';
import { SignInComponent } from './authentification/components/sign-in/sign-in.component';
import { SignOutComponent } from './authentification/components/sign-out/sign-out.component';
import { CommandeCreditsOnlineContainer } from './credit/pages/commander-credits-online/commande-credits-online.container';
import { CommandeCreditsContainer } from './credit/pages/commander-credits/commande-credits.container';
import { HistoriqueServicesContainer } from './historique-services/pages/historique-services.container';
import { RapportContainer } from './rapport/pages/rapport.container';
import { PageNotFoundComponent } from './shared';
import { IsAuthGuard } from './shared/guards/is-auth.guard';
import { IsNotAuthdGuard } from './shared/guards/is-not-auth.guard';
import { RoleGuard } from './shared/guards/role.guard';
import { ContactUsComponent } from './static/pages/contact-us/contact-us.component';
import { HelpSearchComponent } from './static/pages/help-search/help-search.component';
import { LegalNoticesComponent } from './static/pages/legal-notices/legal-notices.component';
import { InfoComponent } from './technique/components/info/info.component';
import { AddCollabComponent } from './utilisateur/components/add-collab/add-collab.component';
import { ValidateCollabComponent } from './utilisateur/components/validate-collab/validate-collab.component';
import { CollaboraterProfileComponent } from './utilisateur/pages/collaborater-profile/collaborater-profile.component';
import { MyProfileComponent } from './utilisateur/pages/my-profile/my-profile.component';
import { PreContractStats } from './statistics/pages/pre-contract-stats/pre-contract-stats.component';
import { LandingPageComponent } from './landing-page/pages/landing-page.component';
import { SignUpComponent } from './authentification/components/sign-up/sign-up.component';
import { featureFlagGuard } from './shared/guards/feature-flag.guard';
import { InscriptionConfirmationComponent } from './authentification/components/sign-up/inscription-confirmation/inscription-confirmation.component';
import { recapitulatifGuard } from './shared/guards/recapitulatif.guard';

const setTitle = (page: string): string => `${page} | Perval.fr`;

export const routes: Routes = [
  ...adminRoutes,
  {
    path: '',
    component: LandingPageComponent,
    canActivate: [IsNotAuthdGuard],
  },
  {
    path: 'inscription',
    canActivate: [IsNotAuthdGuard, featureFlagGuard],
    data: { featureFlag: 'inscription-autonome' },
    children: [
      {
        path: '',
        component: SignUpComponent,
        title: setTitle('Inscription'),
      },
      {
        path: 'confirmation',
        component: InscriptionConfirmationComponent,
        title: setTitle('Confirmation'),
        canActivate: [recapitulatifGuard],
      },
    ],
  },
  {
    path: 'connexion',
    component: SignInComponent,
    canActivate: [IsNotAuthdGuard],
    title: setTitle('Connexion'),
  },
  {
    path: 'deconnexion',
    component: SignOutComponent,
    title: setTitle('Déconnexion'),
  },
  {
    path: 'ajouter-collaborateur',
    component: AddCollabComponent,
    canActivate: [IsAuthGuard, RoleGuard],
    data: {
      expectedRole: [Role.NOTARY],
    },
    title: setTitle('Créer un accès hors ID.NOT'),
  },
  {
    path: 'mot-de-passe-oublie',
    component: ForgotPasswordComponent,
    canActivate: [IsNotAuthdGuard],
    title: setTitle('Mot de passe oublié'),
  },
  {
    path: 'reinitialiser-mot-de-passe',
    component: ResetPasswordComponent,
    canActivate: [IsNotAuthdGuard],
    title: setTitle('Réinitialiser le mot de passe'),
  },
  {
    path: 'valider-collaborateur/:token',
    component: ValidateCollabComponent,
    title: setTitle('Valider mon compte collaborateur'),
  },
  {
    path: 'profil',
    component: MyProfileComponent,
    canActivate: [IsAuthGuard],
  },
  {
    path: 'profil/:login',
    component: CollaboraterProfileComponent,
    canActivate: [IsAuthGuard, RoleGuard],
    data: {
      expectedRole: [Role.NOTARY],
    },
  },
  {
    path: 'commande-de-credits',
    component: CommandeCreditsContainer,
    canActivate: [IsAuthGuard],
  },
  {
    path: 'commander-credits-en-ligne',
    canActivate: [IsAuthGuard, RoleGuard],
    data: {
      expectedRole: [Role.NOTARY],
    },
    children: [
      {
        path: '',
        component: CommandeCreditsOnlineContainer,
      },
      {
        path: 'recapitulatif/:offerId',
        loadComponent: () =>
          import(
            './credit/pages/recapitulatif-paiement/recapitulatif-paiement.container'
          ).then((m) => m.RecapitulatifPaiementContainer),
      },
    ],
  },
  {
    path: 'historique-des-services',
    component: HistoriqueServicesContainer,
    canActivate: [IsAuthGuard, RoleGuard],
    data: {
      expectedRole: [Role.SERVICES_HISTORY_LIST],
    },
  },
  {
    path: 'rapport/:rapport-id',
    component: RapportContainer,
    canActivate: [IsAuthGuard],
  },
  {
    path: 'info',
    component: InfoComponent,
  },
  {
    path: '404',
    component: PageNotFoundComponent,
    title: setTitle('Erreur'),
  },
  {
    path: 'oauth/callback',
    component: OauthCallbackComponent,
  },
  {
    path: 'mentions-legales',
    component: LegalNoticesComponent,
  },
  {
    path: 'contactez-nous',
    component: ContactUsComponent,
  },
  {
    path: 'aide-recherche',
    component: HelpSearchComponent,
  },
  {
    path: 'statistiques-avct',
    component: PreContractStats,
  },
  {
    path: 'paiement',
    canActivate: [IsAuthGuard, RoleGuard],
    data: { expectedRole: [Role.NOTARY] },
    children: [
      {
        path: 'confirmation',
        loadComponent: () =>
          import(
            './credit/pages/paiement-confirmation/paiement-confirmation.container'
          ).then((m) => m.PaiementConfirmationContainer),
        title: setTitle('Confirmation de paiement'),
      },
      {
        path: 'succes',
        loadComponent: () =>
          import(
            './credit/pages/paiement-succes/paiement-succes.container'
          ).then((m) => m.PaiementSuccesContainer),
        title: setTitle('Paiement confirmé'),
      },
      {
        path: 'echec',
        loadComponent: () =>
          import('./credit/pages/paiement-echec/paiement-echec.container').then(
            (m) => m.PaiementEchecContainer,
          ),
        title: setTitle('Échec du paiement'),
      },
    ],
  },
  {
    path: '**',
    pathMatch: 'full',
    redirectTo: '404',
  },
];
